/**
 * @module agent/adapters/services/ToolDetectionService
 *
 * Pure functions for detecting tool execution from AgentCore responses.
 * Zero dependencies - all functions are stateless and side-effect free.
 *
 * Detection strategies:
 * 1. Result structure analysis (AgentCore final result format)
 * 2. OpenTelemetry event parsing (gen_ai.assistant.message events)
 * 3. Legacy string pattern matching (fallback)
 *
 * @example
 * import { detectToolExecution, convertToTableFormat } from './ToolDetectionService';
 *
 * const result = detectToolExecution(chunk);
 * if (result.detected) {
 *   const tableData = convertToTableFormat(result.data, result.toolName);
 * }
 */

// ============================================================================
// TYPE DEFINITIONS (JSDoc)
// ============================================================================

/**
 * @typedef {Object} ToolExecutionResult
 * @property {boolean} detected - Whether tool execution was detected
 * @property {string} [toolName] - Name of the detected tool
 * @property {string} [toolId] - Tool execution ID
 * @property {Object} [params] - Tool parameters
 * @property {Object} [toolResults] - Extracted tool results
 * @property {string} [source] - Detection source ('result_analysis'|'toolUse'|'tool_calls'|'legacy')
 */

/**
 * @typedef {Object} ToolResultsData
 * @property {boolean} detected - Whether results were detected
 * @property {Object} [toolResults] - Parsed results
 * @property {string} [source] - Detection source
 */

/**
 * @typedef {Object} TableData
 * @property {string} type - Always 'table'
 * @property {Object} data - Table content
 * @property {Array<Column>} data.columns - Column definitions
 * @property {Array<Object>} data.rows - Row data
 * @property {Object} data.metadata - Table metadata
 */

/**
 * @typedef {Object} Column
 * @property {string} key - Column key in row data
 * @property {string} label - Display label
 * @property {boolean} sortable - Whether column is sortable
 * @property {boolean} filterable - Whether column is filterable
 * @property {string} type - Column type ('text'|'number'|'currency'|'date'|'url')
 */

// ============================================================================
// MAIN DETECTION FUNCTIONS
// ============================================================================

/**
 * Detect tool execution from AgentCore response chunk.
 * Primary entry point - tries all detection strategies.
 *
 * @param {string} chunkContent - Raw response chunk content
 * @returns {ToolExecutionResult} Detection result
 */
export function detectToolExecution(chunkContent) {
  if (!chunkContent || typeof chunkContent !== 'string') {
    return { detected: false };
  }

  // Strategy 1: Parse AgentCore result structure
  const resultAnalysis = detectFromResultStructure(chunkContent);
  if (resultAnalysis.detected) {
    return resultAnalysis;
  }

  // Strategy 2: Parse OpenTelemetry events
  const eventAnalysis = detectFromOpenTelemetryEvents(chunkContent);
  if (eventAnalysis.detected) {
    return eventAnalysis;
  }

  // Strategy 3: Legacy string pattern matching
  if (detectToolExecutionLegacy(chunkContent)) {
    return {
      detected: true,
      toolName: 'search_companies',
      toolId: 'legacy-detection',
      params: {},
      source: 'legacy'
    };
  }

  return { detected: false };
}

/**
 * Detect tool results from response chunk.
 *
 * @param {string} chunkContent - Raw response chunk content
 * @returns {ToolResultsData} Results detection result
 */
export function detectToolResults(chunkContent) {
  if (!chunkContent || typeof chunkContent !== 'string') {
    return { detected: false };
  }

  // Try OpenTelemetry event parsing first
  const eventResults = detectResultsFromEvents(chunkContent);
  if (eventResults.detected) {
    return eventResults;
  }

  // Fallback to legacy detection
  if (detectToolResultsLegacy(chunkContent)) {
    return {
      detected: true,
      toolResults: { type: 'text', data: chunkContent },
      source: 'legacy'
    };
  }

  return { detected: false };
}

// ============================================================================
// RESULT STRUCTURE ANALYSIS
// ============================================================================

/**
 * Detect tool execution from AgentCore result structure.
 * AgentCore streams final results in format: {"result": {"role": "assistant", "content": [...]}}
 *
 * @param {string} chunkContent - Raw chunk content
 * @returns {ToolExecutionResult} Detection result
 */
export function detectFromResultStructure(chunkContent) {
  try {
    const resultData = JSON.parse(chunkContent);

    if (resultData.result?.role !== 'assistant' || !resultData.result?.content) {
      return { detected: false };
    }

    const content = resultData.result.content;
    let detectedTool = null;
    let extractedResults = null;

    // Analyze text content for tool execution indicators
    if (Array.isArray(content)) {
      const textContent = content
        .filter((item) => item.text)
        .map((item) => item.text)
        .join(' ');

      // Try to extract JSON results from response text first
      // Match JSON array using indexOf to avoid ReDoS (S5852)
      // Safe approach: find array boundaries first, then validate
      const arrayStart = textContent.indexOf('[{');
      const arrayEnd = textContent.lastIndexOf('}]');
      const jsonMatch =
        arrayStart !== -1 && arrayEnd > arrayStart
          ? [textContent.slice(arrayStart, arrayEnd + 2)]
          : null;
      if (jsonMatch) {
        try {
          const rawResults = JSON.parse(jsonMatch[0]);
          if (Array.isArray(rawResults) && rawResults.length > 0) {
            extractedResults = convertToTableFormat(rawResults, 'search');
          }
        } catch {
          // JSON parse failed - continue without results
        }
      }

      // Company search patterns
      const companySearchPatterns = [
        /I found.*companies/i,
        /Here.*the.*companies/i,
        /search.*results.*companies/i,
        /companies.*in.*Germany/i,
        /companies.*in.*Austria/i,
        /matching.*companies/i,
        /Found:.*\[/i // Pattern for "Found: [...]" with JSON
      ];

      if (companySearchPatterns.some((pattern) => pattern.test(textContent))) {
        detectedTool = {
          toolName: 'company_name_search',
          params: { searchKey: extractSearchKey(textContent) }
        };
        // Update searchType in results if we have them
        if (extractedResults) {
          extractedResults.data.metadata.searchType = 'company_search';
        }
      }

      // Check for structured data in content (fallback)
      if (!detectedTool) {
        const structuredContent = content.find(
          (item) => item.data || (item.text?.includes('{'))
        );
        if (structuredContent) {
          detectedTool = { toolName: 'structured_search', params: {} };
        }
      }
    }

    if (detectedTool) {
      return {
        detected: true,
        toolName: detectedTool.toolName,
        params: detectedTool.params,
        toolResults: extractedResults,
        source: 'result_analysis'
      };
    }
  } catch {
    // Not valid JSON - fall through to other strategies
  }

  return { detected: false };
}

// ============================================================================
// OPENTELEMETRY EVENT PARSING
// ============================================================================

/**
 * Detect tool execution from OpenTelemetry events.
 * Parses gen_ai.assistant.message events with toolUse data.
 *
 * @param {string} chunkContent - Raw chunk content
 * @returns {ToolExecutionResult} Detection result
 */
export function detectFromOpenTelemetryEvents(chunkContent) {
  try {
    const eventData = JSON.parse(chunkContent);

    if (eventData.attributes?.['event.name'] !== 'gen_ai.assistant.message') {
      return { detected: false };
    }

    const body = eventData.body;
    if (!body) {
      return { detected: false };
    }

    // Method 1: Direct toolUse object
    if (body.toolUse) {
      return {
        detected: true,
        toolName: body.toolUse.name || 'unknown_tool',
        toolId: body.toolUse.toolUseId,
        params: body.toolUse.input || {},
        source: 'toolUse'
      };
    }

    // Method 2: tool_calls array (OpenAI format)
    if (body.tool_calls?.length > 0) {
      const firstToolCall = body.tool_calls[0];
      return {
        detected: true,
        toolName: firstToolCall.function?.name || 'unknown_tool',
        toolId: firstToolCall.id,
        params: firstToolCall.function?.arguments || {},
        source: 'tool_calls'
      };
    }

    // Method 3: content array with toolUse objects
    if (Array.isArray(body.content)) {
      const toolContent = body.content.find((item) => item.toolUse);
      if (toolContent?.toolUse) {
        return {
          detected: true,
          toolName: toolContent.toolUse.name || 'unknown_tool',
          toolId: toolContent.toolUse.toolUseId,
          params: toolContent.toolUse.input || {},
          source: 'content.toolUse'
        };
      }
    }
  } catch {
    // Not valid JSON or unexpected format
  }

  return { detected: false };
}

/**
 * Detect tool results from OpenTelemetry events.
 *
 * @param {string} chunkContent - Raw chunk content
 * @returns {ToolResultsData} Results detection result
 */
export function detectResultsFromEvents(chunkContent) {
  try {
    const eventData = JSON.parse(chunkContent);

    // Check for tool result in content
    if (eventData.attributes?.['event.name'] === 'gen_ai.assistant.message') {
      const body = eventData.body;

      if (Array.isArray(body?.content)) {
        const resultContent = body.content.find(
          (item) =>
            item.toolResult || (item.text?.includes('[{') && item.text?.includes('"}]'))
        );

        if (resultContent) {
          let parsedResults = null;

          if (resultContent.toolResult) {
            return {
              detected: true,
              toolResults: resultContent.toolResult,
              source: 'toolResult'
            };
          }

          // Try to extract JSON from text using indexOf to avoid ReDoS (S5852)
          if (resultContent.text) {
            const text = resultContent.text;
            const start = text.indexOf('[{');
            const end = text.lastIndexOf('}]');
            const jsonMatch = start !== -1 && end > start ? [text.slice(start, end + 2)] : null;
            if (jsonMatch) {
              try {
                const rawResults = JSON.parse(jsonMatch[0]);
                if (Array.isArray(rawResults) && rawResults.length > 0) {
                  parsedResults = convertToTableFormat(rawResults, 'company_search');
                  return {
                    detected: true,
                    toolResults: parsedResults,
                    source: 'text_json'
                  };
                }
              } catch {
                // JSON parse failed
              }
            }
          }
        }
      }
    }

    // Check for result in body
    if (eventData.body?.result) {
      return {
        detected: true,
        toolResults: { type: 'raw', data: eventData.body.result },
        source: 'body_result'
      };
    }
  } catch {
    // Not valid JSON
  }

  return { detected: false };
}

// ============================================================================
// LEGACY STRING DETECTION (Fallback)
// ============================================================================

/**
 * Legacy string-based tool execution detection.
 * Fallback when structured parsing fails.
 *
 * @param {string} chunk - Raw chunk content
 * @returns {boolean} Whether tool execution was detected
 */
export function detectToolExecutionLegacy(chunk) {
  if (!chunk || typeof chunk !== 'string') {
    return false;
  }

  const toolPatterns = [
    /executing.*search/i,
    /calling.*tool/i,
    /using.*search/i,
    /"tool_use"/i,
    /"function_call"/i,
    /I'll search for/i,
    /using our.*search tool/i
  ];

  return toolPatterns.some((pattern) => pattern.test(chunk));
}

/**
 * Legacy string-based tool results detection.
 *
 * @param {string} chunk - Raw chunk content
 * @returns {boolean} Whether tool results were detected
 */
export function detectToolResultsLegacy(chunk) {
  if (!chunk || typeof chunk !== 'string') {
    return false;
  }

  // Check for JSON array pattern using indexOf to avoid ReDoS (S5852)
  const hasJsonArray = (str) => {
    const start = str.indexOf('[{');
    const end = str.lastIndexOf('}]');
    return start !== -1 && end > start;
  };

  const resultPatterns = [
    (str) => /"result":/i.test(str),
    (str) => /found.*companies/i.test(str),
    (str) => /results.*available/i.test(str),
    hasJsonArray
  ];

  return resultPatterns.some((check) => check(chunk));
}

// ============================================================================
// DATA TRANSFORMATION
// ============================================================================

/**
 * Convert raw results array to table format for AgentDataTable.
 *
 * @param {Array<Object>} rawResults - Array of result objects
 * @param {string} [searchType='search'] - Search type for metadata
 * @returns {TableData|null} Table format data or null if invalid input
 */
export function convertToTableFormat(rawResults, searchType = 'search') {
  if (!Array.isArray(rawResults) || rawResults.length === 0) {
    return null;
  }

  const firstItem = rawResults[0];
  if (!firstItem || typeof firstItem !== 'object') {
    return null;
  }

  const columns = Object.keys(firstItem).map((key) => ({
    key,
    label: formatColumnLabel(key),
    sortable: true,
    filterable: true,
    type: inferColumnType(key, firstItem[key])
  }));

  return {
    type: 'table',
    data: {
      columns,
      rows: rawResults,
      metadata: {
        totalResults: rawResults.length,
        searchType,
        timestamp: new Date().toISOString()
      }
    }
  };
}

/**
 * Format column key as human-readable label.
 *
 * @param {string} key - Column key (e.g., 'company_name')
 * @returns {string} Formatted label (e.g., 'Company Name')
 */
export function formatColumnLabel(key) {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

/**
 * Infer column type from key name and sample value.
 *
 * @param {string} key - Column key
 * @param {any} value - Sample value
 * @returns {string} Column type ('text'|'number'|'currency'|'date'|'url')
 */
export function inferColumnType(key, value) {
  const keyLower = key.toLowerCase();

  // Currency detection
  if (
    keyLower.includes('revenue') ||
    keyLower.includes('value') ||
    keyLower.includes('price') ||
    keyLower.includes('amount') ||
    keyLower.includes('cost')
  ) {
    return 'currency';
  }

  // Number detection
  if (
    keyLower.includes('employee') ||
    keyLower.includes('count') ||
    keyLower.includes('number') ||
    keyLower.includes('quantity') ||
    keyLower.includes('total')
  ) {
    return 'number';
  }

  // Date detection
  if (keyLower.includes('date') || keyLower.includes('year') || keyLower.includes('time')) {
    return 'date';
  }

  // URL detection
  if (keyLower.includes('website') || keyLower.includes('url') || keyLower.includes('link')) {
    return 'url';
  }

  // Value-based detection
  if (typeof value === 'string') {
    if (value.startsWith('http://') || value.startsWith('https://')) {
      return 'url';
    }
  }

  return 'text';
}

/**
 * Extract search key from response text.
 *
 * @param {string} text - Response text
 * @returns {string} Extracted search key or default
 */
export function extractSearchKey(text) {
  if (!text || typeof text !== 'string') {
    return 'search';
  }

  // Try quoted strings first
  const quotedMatch = text.match(/["']([^"']+)["']/);
  if (quotedMatch) {
    return quotedMatch[1];
  }

  // Try capitalized company names
  const capitalizedMatch = text.match(/\b([A-Z][a-zA-Z]+(?:\s+[A-Z][a-zA-Z]+)*)\b/);
  if (capitalizedMatch && capitalizedMatch[1].length > 2) {
    return capitalizedMatch[1];
  }

  return 'company_search';
}

/**
 * Extract tool results from accumulated content.
 * Attempts to parse JSON and extract structured data.
 *
 * @param {string} accumulatedContent - Accumulated response content
 * @returns {TableData|null} Extracted results or null
 */
export function extractToolResults(accumulatedContent) {
  if (!accumulatedContent || typeof accumulatedContent !== 'string') {
    return null;
  }

  try {
    const parsed = JSON.parse(accumulatedContent);

    // Check for table data in AgentCore response format
    if (parsed.result?.data) {
      return {
        type: 'table',
        toolName: 'search_companies',
        data: parsed.result.data
      };
    }

    // Check for companies keyword in JSON content
    if (
      accumulatedContent.toLowerCase().includes('companies') ||
      accumulatedContent.toLowerCase().includes('search')
    ) {
      // Return mock data for development
      return createMockSearchResults();
    }
  } catch {
    // Not valid JSON - no results to extract
  }

  return null;
}

/**
 * Create mock search results for development.
 * @private
 * @returns {TableData} Mock table data
 */
function createMockSearchResults() {
  return {
    type: 'table',
    toolName: 'search_companies',
    data: {
      columns: [
        { key: 'company_name', label: 'Company', sortable: true, type: 'text' },
        { key: 'industry', label: 'Industry', filterable: true, type: 'text' },
        { key: 'revenue', label: 'Revenue', type: 'currency' }
      ],
      rows: [
        { company_name: 'SAP', industry: 'Software', revenue: '€30.9B' },
        { company_name: 'Software AG', industry: 'Software', revenue: '€835M' }
      ],
      metadata: {
        totalResults: 2,
        searchCriteria: 'software consulting, Germany'
      }
    }
  };
}
